import clsx from "clsx";
import React from "react";
import PropTypes from "prop-types";
import { varFadeInUp, varFadeInUpFast } from "./variants/fade/FadeIn";
import { motion, AnimatePresence } from "framer-motion";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog } from "@material-ui/core";

// ----------------------------------------------------------------------

const useStyles = makeStyles((theme) => ({
  root: {},
  paper: {
    borderRadius: theme.shape.borderRadiusMd,
    backgroundColor: theme.palette.background.paper,
  },
}));

// ----------------------------------------------------------------------

DialogAnimate.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  width: PropTypes.string,
  // "fast" | "default" | undefined (auto: rápido en rutas del TPV).
  speed: PropTypes.oneOf(["fast", "default"]),
};

// Rutas operativas (TPV, carta camarero, cobro): ahí cada diálogo se abre
// decenas de veces por servicio y la animación larga se nota como lentitud.
const FAST_ROUTE = /^\/app\/(tpv|tucarta\/tpv|franquiciado\/camarero[^/]*)(\/|$)/;

function prefersReducedMotion() {
  try {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
  } catch {
    return false;
  }
}

export function resolveDialogVariant(speed, pathname) {
  if (speed === "fast") return varFadeInUpFast;
  if (speed === "default") return varFadeInUp;
  if (prefersReducedMotion()) return varFadeInUpFast;
  return FAST_ROUTE.test(pathname || "") ? varFadeInUpFast : varFadeInUp;
}

function DialogAnimate({
  open = false,
  onClose,
  children,
  className,
  width,
  fullScreen = false,
  speed,
  ...other
}) {
  const classes = useStyles();
  const variant = resolveDialogVariant(
    speed,
    typeof window !== "undefined" ? window.location.pathname : "",
  );

  // Cuando se pide fullScreen sobreescribimos las clases del paper —
  // `PaperComponent={motion.div}` no honra siempre las classes que MUI
  // aplica para fullScreen (paperFullScreen) porque el motion.div renderiza
  // div directo sin la cascada de Paper. Inyectando style aquí garantizamos
  // que el modal ocupa el viewport sin márgenes ni borderRadius.
  const fullScreenPaperStyle = fullScreen
    ? {
        width: "100vw",
        maxWidth: "none",
        height: "100vh",
        maxHeight: "none",
        margin: 0,
        borderRadius: 0,
      }
    : undefined;

  return (
    <AnimatePresence>
      {open && (
        <Dialog
          fullWidth
          maxWidth={width ?? "xs"}
          fullScreen={fullScreen}
          open={open}
          onClose={onClose}
          PaperComponent={motion.div}
          PaperProps={{
            ...variant,
            style: { ...(variant.style || {}), ...fullScreenPaperStyle },
          }}
          classes={{ paper: classes.paper }}
          className={clsx(classes.root, className)}
          // Bug clásico mix MUI v4 (Dialog) + MUI v5 (TextField/Autocomplete
          // dentro): el Modal v4 fuerza el foco a su root en cada
          // interacción; los inputs v5 (que viven en otro "árbol" de
          // theme) no aceptan foco y quedan "muertos" al click. Ver
          // OnboardingWizard StepEmpresa — no dejaba escribir en ningún
          // TextField hasta añadir esto. `disableEnforceFocus` deja
          // libre el focus (accesibilidad menor, riesgo asumido: los
          // modals de la app son transaccionales, no lectura larga).
          disableEnforceFocus
          disableRestoreFocus
          {...other}
        >
          {children}
        </Dialog>
      )}
    </AnimatePresence>
  );
}

export default DialogAnimate;
